/**
 * One stock transfer, as moved: a load sheet to print (80 mm for the van,
 * A4 to file), and -- while the goods are still where they went -- the
 * option to undo it. Its number stays spent.
 */
import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/ReceiptLongOutlined'
import PdfIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { useTranslation } from 'react-i18next'

import { cancelTransfer, downloadReportPdf, fetchCompany, fetchTransfer } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import ScreenTitle from '../components/ScreenTitle'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { gregorian, toHijri } from '../reports/hijri'
import { fmtMoney } from '../theme'
import { fmtQty } from './qty'
import { loadSheetHtml, printHtml } from './receipt'
import { CancelDialog, Field } from './bits'

export default function TransferView() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const q = useQuery({ queryKey: ['inv-transfer', Number(id)],
                       queryFn: () => fetchTransfer(Number(id)) })
  const company = useQuery({ queryKey: ['company'], queryFn: fetchCompany })
  const [cancelOpen, setCancelOpen] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const saved = (loc.state as { saved?: boolean } | null)?.saved

  if (q.isLoading) return <Paper variant="outlined" sx={{ p: 3 }}><Skeleton /><Skeleton /></Paper>
  if (q.isError || !q.data) return <Alert severity="warning">{errorMessage(q.error)}</Alert>
  const x = q.data
  const live = x.status === 'POSTED'

  return (
    <Stack spacing={2}>
      {saved && <Alert severity="success">{t('stock.transferSaved', { doc: x.doc_no })}</Alert>}
      {pdfError && <Alert severity="error" onClose={() => setPdfError(null)}>{pdfError}</Alert>}
      <Paper variant="outlined">
        <ScreenTitle titleKey="stock.transfer"
          sub={<>
            <span className="doc-no">{x.doc_no}</span> · {gregorian(x.doc_date, i18n.language)}
            {' · '}{toHijri(x.doc_date, i18n.language)}
          </>}
          right={
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!live && <Chip color="error" variant="outlined" label={t('inv.cancelled')} />}
              <Button variant="contained" startIcon={<PrintIcon />}
                onClick={() => printHtml(loadSheetHtml(x, company.data))}>
                {t('stock.printLoadSheet')}</Button>
              {can(P.reportExport) && (
                <Button variant="outlined" startIcon={<PdfIcon />}
                  onClick={() => downloadReportPdf(`/inventory/transfers/${x.id}/pdf`,
                    { lang: i18n.language === 'ar' ? 'ar' : 'en' },
                    `${x.doc_no.replace(/\//g, '-')}.pdf`).catch((e) => setPdfError(errorMessage(e)))}>
                  {t('inv.printA4')}</Button>
              )}
              {live && can(P.inventoryTransfer) && can(P.reverse) && (
                <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
                  {t('inv.cancel')}</Button>
              )}
              {can(P.inventoryTransfer) && (
                <Button variant="outlined" onClick={() => nav('/inventory/transfers/new')}>
                  {t('stock.newTransfer')}</Button>
              )}
            </Stack>
          } />

        <Box sx={{ px: 2.5, pt: 1, pb: 2, display: 'grid', gap: 2,
                   gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          <Field label={t('stock.from')} value={x.from_store_name}
            sub={x.from_store_kind === 'VAN' ? t('inv.kindVAN') : undefined} />
          <Field label={t('stock.to')} value={x.to_store_name}
            sub={x.to_store_kind === 'VAN' ? t('inv.kindVAN') : undefined} />
          <Field label={t('stock.enteredBy')} value={x.entered_by ?? '—'} />
          {x.notes && <Field label={t('inv.notes')} value={x.notes} />}
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 600 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th className="num" style={{ width: 120 }}>{t('inv.qty')}</th>
              <th className="num" style={{ width: 130 }}>{t('stock.inBase')}</th>
              {x.value !== undefined && <th className="num" style={{ width: 120 }}>{t('inv.value')}</th>}
            </tr></thead>
            <tbody>
              {x.lines.map((l) => (
                <tr key={l.id}>
                  <td className="num" style={{ color: 'var(--ink-muted)' }}>{l.line_no}</td>
                  <td dir="auto"><span className="doc-no">{l.item_code}</span> · {(ar && l.item_name_ar) || l.item_name}</td>
                  <td className="num">{fmtQty(l.qty)} <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>{l.unit}</span></td>
                  <td className="num" style={{ color: 'var(--ink-muted)' }}>{fmtQty(l.base_qty)} {l.base_unit}</td>
                  {l.value !== undefined && <td className="num">{fmtMoney(l.value)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box sx={{ pt: 2 }}>
          <FigureGrid>
            <Figure label={bi('inv.lines')} value={String(x.lines.length)} />
            {x.value !== undefined && <Figure label={bi('inv.value')} value={fmtMoney(x.value)} strong />}
          </FigureGrid>
          <Typography sx={{ px: 2.5, pb: 2, fontSize: 12.5, color: 'var(--ink-muted)' }}>
            {t('stock.transferNote')}</Typography>
        </Box>
      </Paper>

      <CancelDialog open={cancelOpen} title={t('inv.cancelTitle', { doc: x.doc_no })}
        body={t('stock.cancelTransferBody')} onClose={() => setCancelOpen(false)}
        run={(reason) => cancelTransfer(x.id, reason)}
        onDone={() => {
          setCancelOpen(false)
          void qc.invalidateQueries({ queryKey: ['inv-transfer'] })
          void qc.invalidateQueries({ queryKey: ['inv-transfers'] })
          void qc.invalidateQueries({ queryKey: ['inv-items'] })
        }} />
    </Stack>
  )
}
